"use client";

import FavoriteIconButton from "@/components/card/card-component/FavoriteIconButton";
import RatingReviewBadge from "@/components/card/card-component/RatingReviewBadge";
import Image from "next/image";
import { memo } from "react";
import { Link } from "@/i18n/navigation";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Typography } from "../Typography";
import { ServiceIcon } from "../services/ServiceMeta";
import CardStatusBadges from "./card-component/CardStatusBadges";
import { SpaListingCaruselItem } from "../main-services/spa-msg/spa-msg.type";
import PriceDisplay from "./card-component/PriceDisplay";
import { useRouter } from "next/navigation";
import { useSpaHref } from "@/hooks/url-state/useSpaQuery";

export type SpaMassageCardProps = {
  data: SpaListingCaruselItem;
  className?: string;
  href?: string;
};

const SpaMassageCard = ({ data, className, href }: SpaMassageCardProps) => {
  const router = useRouter();
  const finalHref = useSpaHref(data, href);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("a")) {
      return;
    }
    router.push(finalHref as never);
  };

  const cardContent = (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white">
      {/* Image */}
      <section className="relative h-[190px] w-full shrink-0 overflow-hidden rounded-t-2xl sm:h-[200px]">
        <Image
          src={data.feature ?? FALLBACK_FEATURE_IMAGE}
          alt={data.title ?? "N/A"}
          fill
          unoptimized
          className="transform-gpu object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          sizes="(max-width: 640px) 100vw, 320px"
        />

        <FavoriteIconButton
          itemId={Number(data.id)}
          svcType="spa_msg"
          ariaLabel="Save service"
          active={data.isFavorite}
          className="absolute left-2 top-2 z-10"
        />

        <CardStatusBadges
          isInstantConfirm={data.isInstantConfirm}
          className="absolute right-2 top-2 z-10"
        />

        <RatingReviewBadge
          className="absolute bottom-2 left-2"
          rating={data.overallReview?.overallRating}
          reviewCount={data.totalReviews}
        />

        <CardStatusBadges
          disPercent={data.disPercent}
          className="absolute bottom-2 right-2 z-10"
        />
      </section>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col px-3 py-2.5 space-y-2">
        <div className="h-15 space-y-0.5">
          <Typography
            variant="subtitle1"
            className="line-clamp-2 group-hover:text-secondary"
          >
            {data.title}
          </Typography>

          {data.duration ? (
            <div className="flex min-w-0 items-center gap-1">
              <ServiceIcon
                icon="clock"
                className="h-3.5 w-3.5 shrink-0 text-body"
              />

              <Typography variant="body2" className="truncate text-body">
                {data.duration}
              </Typography>
            </div>
          ) : null}
        </div>

        {/* Bottom Information */}
        <section className="mt-auto flex min-w-0 items-end justify-between gap-2">
          {data.located ? (
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <ServiceIcon
                icon="location"
                className="h-3.5 w-3.5 shrink-0 text-body"
              />

              <Typography variant="body2" className="truncate text-body">
                {data.located}
              </Typography>
            </div>
          ) : null}

          <span className="bg-[#EDEEEE40] py-1 px-2 rounded-xl">
            <PriceDisplay
              price={data.price}
              salePrice={data.salePrice}
              className="shrink-0 justify-end"
              salePriceColor="text-primary"
            />
          </span>
        </section>
      </div>
    </div>
  );

  return (
    <article
      className={cn(
        "group relative w-full rounded-2xl border shadow-product-card",
        className,
      )}
      onClick={handleCardClick}
    >
      {finalHref ? (
        <Link
          href={finalHref as never}
          className="block h-full"
          aria-label={`Open ${data.title}`}
        >
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
    </article>
  );
};

export default memo(SpaMassageCard);
